'use client';

import Image from 'next/image';
import React, { useRef, useState, useEffect } from 'react';
import { useAutosizeTextArea, useUser } from '@/lib/hooks';
import { EditIcon, IconAvatar, IconSave } from '@/components/common/icons/SVGs';
import { Textarea } from '@/components/common/inputs/textarea/textarea';
import { cn, isValidUrl } from '@/lib/utils';
import { UserQuestionProps } from '@/types';

/**
 * UserQuestion component displays a user's question with optional editing functionality
 * @param {Object} props - Component props
 * @param {Object} props.advice - Advice object containing question details
 * @param {string} props.title - Title to display if no advice question exists
 * @param {boolean} props.showEdit - Whether to show edit controls
 */
export function UserQuestion({ advice, title, showEdit = false }: UserQuestionProps) {
  const { user } = useUser();
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);
  const [inputValue, setInputValue] = useState(advice?.question || title || '');
  const promptInputRef = useRef<HTMLTextAreaElement>(null);

  useAutosizeTextArea(promptInputRef.current, inputValue);
  const isTextInInput = inputValue.trim().length > 0;

  const handleUpdateQuestion = async () => {
    setIsEditingPrompt(false);
  };

  useEffect(() => {
    const newValue = advice?.question || title || '';
    if (newValue !== inputValue) {
      setInputValue(newValue);
    }
  }, [advice?.question, title, inputValue]);

  if (!advice?.question && !title) {
    return (
      <div className="flex items-center justify-between rounded-lg border border-purple-100 dark:border-purple-900/50 bg-purple-100 dark:bg-purple-900/20 p-2.5 sm:p-3.5 md:p-4 shadow-lg dark:shadow-purple-900/10">
        <div className="flex w-full items-center space-x-2 sm:space-x-3">
          <div className="relative h-7 w-7 sm:h-8 sm:w-8 md:h-10 md:w-10 min-w-[28px] sm:min-w-[32px] md:min-w-[40px] overflow-hidden rounded-full bg-muted dark:bg-opacity-20">
            <div className="flex h-full items-center justify-center bg-purple-500 dark:bg-purple-700 text-white">
              <IconAvatar height={16} width={16} className="sm:h-5 sm:w-5 md:h-6 md:w-6" />
            </div>
          </div>
          <span className="flex-wrap items-center text-sm sm:text-base font-medium text-gray-700 dark:text-gray-100 line-clamp-2 sm:line-clamp-none">
            Loading question...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between rounded-lg border border-purple-100 dark:border-purple-900/50 bg-purple-100 dark:bg-purple-900/20 p-2.5 sm:p-3.5 md:p-4 shadow-lg dark:shadow-purple-900/10">
      <div className="flex w-full items-center space-x-2 sm:space-x-3">
        {/* User avatar */}
        <div className="relative h-7 w-7 sm:h-8 sm:w-8 md:h-10 md:w-10 min-w-[28px] sm:min-w-[32px] md:min-w-[40px] overflow-hidden rounded-full bg-muted dark:bg-opacity-20">
          {user?.avatar && isValidUrl(user.avatar) ? (
            <Image src={user.avatar} alt="User avatar" fill sizes="100%" />
          ) : (
            <div className="flex h-full items-center justify-center bg-purple-500 dark:bg-purple-700 text-white">
              <IconAvatar height={16} width={16} className="sm:h-5 sm:w-5 md:h-6 md:w-6" />
            </div>
          )}
        </div>

        {/* Render text or editing textarea */}
        {isEditingPrompt ? (
          <div className="relative w-full">
            <Textarea
              onChange={(e) => setInputValue(e.target.value)}
              value={inputValue}
              ref={promptInputRef}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleUpdateQuestion();
                }
              }}
              className={cn(
                isTextInInput ? '' : '!h-[32px]',
                'hide-scrollbar rounded-lg border border-input dark:border-opacity-30 p-2 sm:p-2.5 md:p-3 pr-8 sm:pr-10 md:pr-12 text-sm sm:text-base placeholder:text-muted-foreground dark:placeholder:text-gray-400 focus:border-purple-600 dark:focus:border-purple-500 dark:bg-gray-900/30 dark:text-gray-100'
              )}
            />
          </div>
        ) : (
          <span className="flex-wrap items-center text-sm sm:text-base font-medium text-gray-700 dark:text-gray-100 line-clamp-2 sm:line-clamp-none">
            {advice?.question || title}
          </span>
        )}
      </div>

      {/* Edit/Save/Cancel controls */}
      {showEdit && (
        <div className="ml-2 sm:ml-3 flex-col items-center space-y-2">
          <button
            onClick={() => {
              if (isEditingPrompt) handleUpdateQuestion();
              else setIsEditingPrompt(true);
            }}
            className="text-muted-foreground dark:text-gray-300 transition hover:text-purple-600 dark:hover:text-purple-400"
          >
            {isEditingPrompt ? (
              <IconSave height={14} width={14} className="sm:h-4 sm:w-4 md:h-5 md:w-5" />
            ) : (
              <EditIcon color="currentColor" className="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5" />
            )}
          </button>
          {isEditingPrompt && (
            <button
              onClick={() => setIsEditingPrompt(false)}
              className="text-muted-foreground dark:text-gray-300 transition hover:text-red-500 dark:hover:text-red-400"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
